import type { DirectiveBinding } from 'vue'
import { useAuthState } from '@/composables/useAuthState'
import type { Role } from '@/constants/roles'

/**
 * Custom directive for role-based element visibility
 *
 * Shows or hides DOM elements based on the user's roles. Elements are hidden
 * using display: none if the user lacks the required permissions.
 *
 * @example
 * <!-- Single role - show only to super admins -->
 * <v-btn v-permission="ROLES.ADMIN_SUPER">Super Admin Only</v-btn>
 *
 * @example
 * <!-- Multiple roles with any mode (default) - show if user has ANY of the roles -->
 * <v-btn v-permission="[ROLES.ADMIN_SUPER, ROLES.ADMIN_MANAGER]">
 *   Admins Only
 * </v-btn>
 *
 * @example
 * <!-- Multiple roles with all mode - show only if user has ALL of the roles -->
 * <v-btn v-permission:all="[ROLES.ADMIN_SUPER, ROLES.SERVICE_PROVIDER]">
 *   Must Have Both Roles
 * </v-btn>
 *
 * @example
 * <!-- Explicit any mode (same as default) -->
 * <v-btn v-permission:any="[ROLES.ADMIN_SUPER, ROLES.ADMIN_MANAGER]">
 *   Admins Only
 * </v-btn>
 */

/**
 * Type for directive mode argument
 */
type PermissionMode = 'any' | 'all'

/**
 * Check if element should be visible based on permissions
 *
 * @param binding - Vue directive binding
 * @returns true if element should be visible, false otherwise
 */
function checkPermission(binding: DirectiveBinding<Role | Role[]>): boolean {
  const authState = useAuthState()

  // Hide if user is not authenticated
  if (!authState.isAuthenticated.value) {
    return false
  }

  // Normalize to array for consistent processing
  const roles = Array.isArray(binding.value) ? binding.value : [binding.value]

  // Handle empty/invalid roles - hide element
  if (!roles || roles.length === 0) {
    return false
  }

  // Get mode: 'any' (default) or 'all'
  const mode = (binding.arg as PermissionMode) || 'any'

  // Check permissions based on mode
  if (mode === 'all') {
    // All roles required - every role must be present
    return roles.every(role => authState.hasRole(role))
  } else {
    // Any role sufficient (default) - at least one role must be present
    return authState.hasAnyRole(...roles)
  }
}

/**
 * Update element visibility based on permission check
 *
 * @param el - DOM element to show/hide
 * @param binding - Vue directive binding
 */
function updateVisibility(
  el: HTMLElement,
  binding: DirectiveBinding<Role | Role[]>
) {
  const hasPermission = checkPermission(binding)

  if (hasPermission) {
    // Show element - remove display: none
    el.style.display = ''
  } else {
    // Hide element - set display: none
    el.style.display = 'none'
  }
}

/**
 * Permission directive implementation
 *
 * Hooks:
 * - mounted: Initial permission check when element is added to DOM
 * - updated: Re-check permissions when component updates (handles auth state changes)
 */
export const vPermission = {
  mounted(el: HTMLElement, binding: DirectiveBinding<Role | Role[]>) {
    updateVisibility(el, binding)
  },

  updated(el: HTMLElement, binding: DirectiveBinding<Role | Role[]>) {
    updateVisibility(el, binding)
  },
}
